import React, { useState, useEffect } from "react";
import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { Review } from "./PlaceDetail";
import useUserStore from "../store/userStore";
import ModalTemplate from "./ModalWrapper";
import SignUp from "./SignUp";
import Login from "./Login";
import { ReactComponent as EditIcon } from "../assets/icons/pencil-01.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface WriteCommentProps {
  className?: string;
  placeId?: string;
  userId?: string;
  onReviewSubmitted?: (newReview: Review) => void;
}

const WriteComment: React.FC<WriteCommentProps> = ({
  className = "",
  placeId,
  userId,
  onReviewSubmitted,
}) => {
  const user = useUserStore((state) => state.user);

  const [name, setName] = useState(user?.fullName || user?.username || "");
  const [email, setEmail] = useState(user?.email || "");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);

  const [modalType, setModalType] = useState<"login" | "signup" | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.fullName || user.username || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setModalType("login");
      return;
    }

    const currentUserId = user?.userId || userId;

    if (!placeId || !currentUserId) {
      setErrorMessage("شناسه مکان یا کاربر مشخص نیست.");
      return;
    }

    if (!comment.trim()) {
      setErrorMessage("لطفاً نظر خود را بنویسید.");
      return;
    }

    if (!rating) {
      setErrorMessage("لطفاً امتیاز خود را انتخاب کنید.");
      return;
    }


    setLoading(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${BASE_URL}/api/reviews/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          place: placeId,
          user: currentUserId,
          rate: rating,
          comment: comment,
        }),
      });

      if (!res.ok) throw new Error("خطا در ثبت دیدگاه");

      const newReviewData = await res.json();

      const currentUserName = user?.fullName || user?.username || "کاربر سایت";

      const newReview: Review = {
        review_id: newReviewData.review_id || newReviewData._id || Date.now().toString(),
        place: placeId,
        place_name: newReviewData.place_name || "",
        user: currentUserId,
        user_name: currentUserName,
        full_name: currentUserName,
        rate: rating,
        comment: comment,
        date: new Date().toISOString(),
      };

      if (onReviewSubmitted) {
        onReviewSubmitted(newReview);
      }

      setSuccessMessage(true);
      setTimeout(() => setSuccessMessage(false), 3000);

      setComment("");
      setRating(0);
    } catch (err: any) {
      console.error("❌ خطا در ثبت کامنت:", err);
      setErrorMessage(err.message || "خطا در ارسال دیدگاه.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={`w-full mt-[16px] ${className}`}>

        {errorMessage && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-sm">
            <span className="text-sm font-myYekanFaNumMedium">
              {errorMessage}
            </span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-sm">
            <span className="text-sm font-myYekanFaNumMedium">
              دیدگاه شما با موفقیت ثبت شد
            </span>
          </div>
        )}
        <div className="relative mb-4 mt-2 w-full">
          <textarea
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
            rows={5}
            placeholder="درباره تجربه خود بنویسید..."
            className="bg-[#F6FDFE] w-full h-[140px] px-4 py-4 pr-[16px] rounded-lg resize-none font-myYekanFaNumRegular text-[12px]"
            dir="rtl"
          />

          <div className="absolute bottom-3 left-3 flex items-center gap-4">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  {star <= rating ? (
                    <StarSolid className="h-6 w-6 text-yellow-400" />
                  ) : (
                    <StarOutline className="h-6 w-6 text-gray-400 hover:text-yellow-400" />
                  )}
                </button>
              ))}
            </div>
            <button
              type="submit"
              className="border border-[#4793A8] text-[#4793A8] hover:bg-[#4793A8]/10  text-sm px-4 py-2 rounded-full font-myYekanFaNumMedium disabled:opacity-50 flex items-center gap-2"
              disabled={loading}
            >
              {loading ? "در حال ارسال..." : "نوشتن نظر"}
              <div className="group">
                <EditIcon className="w-4 h-4 text-[#4793A8]" />
              </div>           </button>
          </div>
        </div>

      </form>

      {modalType && (
        <ModalTemplate
          showModal={true}
          onClose={() => setModalType(null)}
          mainComponent={
            modalType === "login" ? (
              <Login
                onClose={() => setModalType(null)}
                switchToSignup={() => setModalType("signup")}
              />
            ) : (
              <SignUp
                onClose={() => setModalType(null)}
                switchToLogin={() => setModalType("login")}
              />
            )
          }
        />
      )}
    </>
  );
};

export default WriteComment;